import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { Loader, InfoIcon, Mail } from "lucide-react";
import { toast } from "sonner";
import { InputGroup, InputGroupInput, InputGroupAddon } from "@/taskpane/components/ui/input-group";
import { Button } from "@/taskpane/components/ui/button";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/taskpane/components/ui/field";
import { useSendResetPasswordEmail } from "@/hooks/useSendResetPasswordEmail";
import {
  forgotPasswordEmailSchema,
  type ForgotPasswordEmailFormData,
} from "@/schemas/auth.schemas";
import OnboardingHeader from "../OnboardingHeader";

interface ForgotPasswordEmailProps {
  onBackToLogin: () => void;
  onEmailSent: (email: string) => void;
}

interface EmailFieldProps {
  control: ReturnType<typeof useForm<ForgotPasswordEmailFormData>>["control"];
  isPending: boolean;
}

const EmailField: React.FC<EmailFieldProps> = ({ control, isPending }) => (
  <FieldGroup>
    <Controller
      name="email"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className="gap-1">
          <FieldLabel className="font-medium text-sm leading-5 text-neutral-400">Email</FieldLabel>
          <FieldContent>
            <InputGroup aria-invalid={fieldState.invalid}>
              <InputGroupAddon>
                <Mail className="text-neutral-200" />
              </InputGroupAddon>
              <InputGroupInput
                type="email"
                placeholder="Enter your email"
                {...field}
                disabled={isPending}
                aria-describedby={fieldState.invalid ? "email-error" : undefined}
                aria-invalid={fieldState.invalid}
                autoComplete="email"
                className="placeholder:text-neutral-500 text-sm py-3 px-4"
              />
            </InputGroup>
            {fieldState.invalid && (
              <div className="flex items-center gap-1 mt-1" id="email-error">
                <InfoIcon className="inline size-3 text-danger-200" />
                <FieldError className="leading-4" errors={[fieldState.error]} />
              </div>
            )}
          </FieldContent>
        </Field>
      )}
    />
  </FieldGroup>
);

interface ForgotPasswordFormProps {
  control: ReturnType<typeof useForm<ForgotPasswordEmailFormData>>["control"];
  isPending: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  control,
  isPending,
  onSubmit,
}) => (
  <form
    onSubmit={onSubmit}
    className="flex flex-col gap-4 w-full"
    noValidate
    aria-label="Forgot password form"
  >
    <EmailField control={control} isPending={isPending} />
    <Button type="submit" size="lg" className="mt-2" disabled={isPending}>
      {isPending ? (
        <>
          <Loader className="animate-spin mr-2" />
          Sending...
        </>
      ) : (
        "Send Reset Link"
      )}
    </Button>
  </form>
);

const ForgotPasswordEmail: React.FC<ForgotPasswordEmailProps> = ({
  onBackToLogin,
  onEmailSent,
}) => {
  const sendResetEmailMutation = useSendResetPasswordEmail();

  const { control, handleSubmit } = useForm<ForgotPasswordEmailFormData>({
    resolver: zodResolver(forgotPasswordEmailSchema),
    mode: "onBlur",
    defaultValues: { email: "" },
  });

  const extractErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
      const data = error.response?.data as { detail?: string; error?: string } | undefined;
      return data?.detail ?? data?.error ?? "Something went wrong. Please try again.";
    }
    return "Something went wrong. Please try again.";
  };

  const onFormSubmit = async (data: ForgotPasswordEmailFormData) => {
    try {
      await sendResetEmailMutation.mutateAsync({ email: data.email });
      onEmailSent(data.email);
    } catch (error) {
      console.error("Failed to send reset password email:", error);
      toast.error(extractErrorMessage(error));
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-md mx-auto">
      <OnboardingHeader onBack={onBackToLogin} />
      <section
        className="flex flex-col items-center gap-6 w-full"
        role="main"
        aria-labelledby="forgot-password-heading"
        aria-describedby="forgot-password-description"
      >
        <div className="text-center">
          <h2 id="forgot-password-heading" className="text-xl font-medium m-0 mb-2">
            Forgot Password
          </h2>
          <p
            id="forgot-password-description"
            className="text-sm text-neutral-400 m-0 leading-normal"
          >
            Enter your email address and we&apos;ll send you a link to reset your password
          </p>
        </div>
        <ForgotPasswordForm
          control={control}
          isPending={sendResetEmailMutation.isPending}
          onSubmit={handleSubmit(onFormSubmit)}
        />
      </section>
    </div>
  );
};

export default ForgotPasswordEmail;
